import { useCallback, useEffect, useRef, useState } from 'react';
import { Loadable } from '../../types';
import { validateMe } from './certreqHttpClient';
import { ValidateMeRequest, ValidateMeResult } from './types';

export type CertReqErrorKind = 'notfound' | 'forbidden' | 'gateway' | 'unknown';

export type CertReqValidationError = {
  kind: CertReqErrorKind;
  status?: number;
  /** Ferdig brukervendt tekst fra backend (særlig ved 502). */
  message?: string;
};

const kindFromStatus = (status?: number): CertReqErrorKind => {
  if (status === 404) return 'notfound';
  if (status === 403 || status === 401) return 'forbidden';
  if (status === 502) return 'gateway';
  return 'unknown';
};

/** Backend kan svare med ProblemDetails, et objekt eller ren tekst. */
const readErrorMessage = (data: any): string | undefined => {
  if (!data) return undefined;
  if (typeof data === 'string') return data || undefined;
  return data.detail || data.title || data.message || data.error || undefined;
};

export type CertReqValidation = {
  validation: Loadable<ValidateMeResult, CertReqValidationError>;
  reload: () => void;
};

/**
 * Henter kravtreet for innlogget person mot én sertifikatkode.
 * Følger Loadable-mønsteret i klienten — ingen react-query.
 *
 * Valideringen kjøres bare når kravkoden (eller vurderingsdatoen) faktisk endrer
 * seg. Grunnen er at siden laster søknaden på nytt hver gang et vedlegg lastes
 * opp: `application.result` tømmes et lite øyeblikk, og kravkoden går da fra
 * «D1» til «» og tilbake til «D1». Uten vaktposten her ville hver filopplasting
 * utløst fem kall til kilderegistrene og en full motorkjøring. Det ville
 * dessuten vært meningsløst: motoren vurderer kun registerdata, og en opplastet
 * fil går til saksbehandler uten å kunne endre motorens svar.
 *
 * Vaktposten er nøkkelen (kode + dato) vi sist STARTET en kjøring for. En tom
 * kode gjør ingenting — verken nytt kall eller tømming av resultatet — mens et
 * ekte kodebytte gir en ny, ikke-tom nøkkel og slår gjennom. `reload` tvinger
 * alltid en ny kjøring, slik at retry-lenken ved feil fortsatt virker.
 */
export const useCertReqValidation = (
  certificateCode?: string | null,
  request?: ValidateMeRequest
): CertReqValidation => {
  const [validation, setValidation] = useState<Loadable<ValidateMeResult, CertReqValidationError>>(
    {}
  );
  /** Nøkkelen (kode + dato) siste kjøring ble startet for. Vaktpost mot dobbeltkjøring. */
  const loadedKey = useRef<string | null>(null);
  /** Løpenummer per kall, slik at et sent svar aldri overskriver et nyere. */
  const requestId = useRef<number>(0);

  const evaluationDate = request?.evaluationDate;

  const run = useCallback(
    (force: boolean) => {
      const code = certificateCode || '';
      // Tom kode betyr enten at søknaden ikke er hentet ennå, eller at den
      // lastes på nytt etter en filopplasting. I begge tilfeller er det
      // ingenting nytt å hente, og vi beholder det vi allerede viser.
      if (!code) return;

      const key = `${code}|${evaluationDate || ''}`;
      // Samme kode og samme dato som sist — da har vi allerede svaret, eller et
      // kall underveis. Bare reload får tvinge en ny kjøring.
      if (!force && loadedKey.current === key) return;

      loadedKey.current = key;
      requestId.current += 1;
      const currentRequest = requestId.current;

      setValidation({ loading: true });
      validateMe(code, evaluationDate ? { evaluationDate } : undefined)
        .then((response) => {
          if (currentRequest !== requestId.current) return;
          setValidation({ result: response.data });
        })
        .catch((e: any) => {
          if (currentRequest !== requestId.current) return;
          const status = e?.response?.status;
          setValidation({
            error: {
              kind: kindFromStatus(status),
              status,
              message: readErrorMessage(e?.response?.data),
            },
          });
        });
    },
    [certificateCode, evaluationDate]
  );

  useEffect(() => {
    run(false);
  }, [run]);

  const reload = useCallback(() => run(true), [run]);

  return { validation, reload };
};
